import {Map, Record} from "immutable"
import {Employee, Employees, Fired, fromJs as employeeFromJs, JsonData as EmployeeJsonData} from "./Employee"
import {fromJs as yearFromJs, JsonData as YearJsonData, Year, Years} from "./Year"
import {fromJs as studentFromJs, JsonData as StudentJsonData, Students} from "./Student"
import {fromJs as vacancyFromJs, JsonData as VacancyJsonData, Vacancies, Vacancy} from "./Vacancy"
import {DEMAND_YEARS, EMPLOYEE_YEAR, ENTRANCE_YEARS, TRAINEE_DEMAND_YEARS} from "../Demand/common";
import {fromJs as traineeFromJs, JsonData as TraineeJsonData, Trainees} from "./Trainee";
import {Specialities, Speciality} from "./Speciality";

export enum CourseOfStudy {
    Intramural,
    Distance,
    IntramuralAndDistance,
}



export type DemandByYear = {[key: number]: number}

const courseOfStudyStringToEnum = {
    "Очная": CourseOfStudy.Intramural,
    "Заочная": CourseOfStudy.Distance,
    "Очно-заочная": CourseOfStudy.IntramuralAndDistance,
}

export const coursesOfStudy = {
    [CourseOfStudy.Intramural]: "Очная",
    [CourseOfStudy.Distance]: "Заочная",
    [CourseOfStudy.IntramuralAndDistance]: "Очно-заочная",
}

interface Properties {
    id: number
    companyId: number
    specialityId: number
    regionId: number
    employmentRegionId: number
    universityBranchId: number
    addBachelorDemand: true
    addBachelorTarget: boolean
    months: string
    isPracticePaid: boolean
    isPracticePaidName: string
    businessType: string
    businessTypeName: string
    nationalProject: string
    nationalProjectName: string
    englishLevel: string
    englishLevelName: string
    language: string
    languageName: string
    courseOfStudy: CourseOfStudy
    entranceFactorDemand: number
    entranceFactorTarget: number
    notes: string
    countryId: string
    countryName: string
    employees: Employees
    trainees: Trainees
    years: Years
    vacancies: Vacancies
    students: Students
}

export class Demand extends Record({
    id: undefined,
    companyId: undefined,
    specialityId: undefined,
    regionId: undefined,
    employmentRegionId: undefined,
    universityBranchId: undefined,
    courseOfStudy: undefined,
    entranceFactorDemand: undefined,
    entranceFactorTarget: undefined,
    addBachelorDemand: undefined,
    addBachelorTarget: undefined,
    months: undefined,
    isPracticePaid: undefined,
    isPracticePaidName: undefined,
    businessType: undefined,
    businessTypeName: undefined,
    nationalProject: undefined,
    nationalProjectName: undefined,
    englishLevel: undefined,
    englishLevelName: undefined,
    language: undefined,
    languageName: undefined,
    speciality: undefined,
    notes: undefined,
    countryId: undefined,
    countryName: undefined,
    trainees: Map(),
    employees: Map(),
    years: Map(),
    vacancies: Map(),
    students: Map(),
}) implements Properties {
    readonly id: number
    readonly companyId: number
    readonly specialityId: number
    readonly regionId: number
    readonly employmentRegionId: number
    readonly universityBranchId: number
    readonly addBachelorDemand: true
    readonly addBachelorTarget: boolean
    readonly months: string
    readonly isPracticePaid: boolean
    readonly isPracticePaidName: string
    readonly businessType: string
    readonly businessTypeName: string
    readonly nationalProject: string
    readonly nationalProjectName: string
    readonly englishLevel: string
    readonly englishLevelName: string
    readonly language: string
    readonly languageName: string
    readonly courseOfStudy: CourseOfStudy
    readonly entranceFactorDemand: number
    readonly entranceFactorTarget: number
    readonly notes: string
    readonly countryId: string
    readonly countryName: string
    readonly trainees: Trainees
    readonly employees: Employees
    readonly years: Years
    readonly vacancies: Vacancies
    readonly students: Students
    readonly speciality: Speciality

    get courseOfStudyName(): string {
        return coursesOfStudy[this.courseOfStudy]
    }

    private _traineeByYear: DemandByYear
    private _demandByYear: DemandByYear
    private _demandOnlyByYear: DemandByYear
    private _vacancesByYear: DemandByYear
    private _traineeDemandByYear: DemandByYear
    private _targetByYear: DemandByYear
    private _targetRequestByYear: DemandByYear
    private _entranceByYear: DemandByYear

    get traineeByYear(): DemandByYear {
        if (this._traineeByYear === undefined) {
            this._traineeByYear = this.years
                .filter(({isTraineeDemand}: Year) => isTraineeDemand())
                .map(({year, demand}: Year) => [year, demand]).toArray()
                .reduce(
                    (o, [c, v]) => {
                        o[c] = v
                        return o;
                    },
                    <DemandByYear>{},
                )
        }

        return this._traineeByYear
    }

    get demandByYear(): DemandByYear {
        if (this._demandByYear === undefined) {
            this._demandByYear = this.years.filter(({isDemand}: Year) => isDemand()).map(({year, demand}: Year) => [year, demand]).toArray().reduce((o, [c, v]) => {
                if (DEMAND_YEARS.includes(c)) {
                    o[c] = v
                }
                return o;
            }, <DemandByYear>{})

            this.vacancies.map(({year, demand}: Vacancy) => {
                this._demandByYear[year] = (this._demandByYear[year] || 0) + demand
            })
        }

        return this._demandByYear
    }

    get vacancesByYear(): DemandByYear {
        if (this._vacancesByYear === undefined) {
            this.vacancies.map(({year, demand}: Vacancy) => {
                this._vacancesByYear[year] = (this._vacancesByYear[year] || 0) + demand
            })
        }

        return this._demandByYear
    }

    get demandOnlyByYear(): DemandByYear {
        if (this._demandOnlyByYear === undefined) {
            this._demandOnlyByYear = this.years.filter(({isDemand}: Year) => isDemand()).map(({year, demand}: Year) => [year, demand]).toArray().reduce((o, [c, v]) => {
                if (DEMAND_YEARS.includes(c)) {
                    o[c] = v
                }
                return o;
            }, <DemandByYear>{})
        }

        return this._demandOnlyByYear
    }

    get traineeDemandByYear(): DemandByYear {
        if (this._traineeDemandByYear === undefined) {
            this._traineeDemandByYear = this.years.filter(({isTraineeDemand}: Year) => isTraineeDemand()).map(({year, demand}: Year) => [year, demand]).toArray().reduce((o, [c, v]) => {
                if (TRAINEE_DEMAND_YEARS.includes(c)) {
                    o[c] = v
                }
                return o;
            }, <DemandByYear>{})
        }

        return this._traineeDemandByYear
    }

    get targetByYear(): DemandByYear {
        if (this._targetByYear === undefined) {
            this._targetByYear = this.years.filter(({isTarget}: Year) => isTarget()).map(({year, demand}: Year) => [year + Math.ceil(this.speciality.period), demand]).toArray().reduce((o, [c, v]) => {
                o[c] = v
                return o;
            }, <DemandByYear>{})
        }

        return this._targetByYear
    }

    get targetRequestByYear(): DemandByYear {
        if (this._targetRequestByYear === undefined) {
            this._targetRequestByYear = this.years.filter(({isTarget}: Year) => isTarget()).map(({year, demand}: Year) => [year, demand]).toArray().reduce((o, [c, v]) => {
                o[c] = v
                return o;
            }, <DemandByYear>{})
        }

        return this._targetRequestByYear
    }

    get entranceByYear(): DemandByYear {
        if (this._entranceByYear === undefined) {
            this._entranceByYear = {}
            const yearDemand: DemandByYear = {}

            this.years.forEach(({year, demand, isDemand}: Year) => {
                if (isDemand()) {
                    yearDemand[year] = demand
                }
            })

            this.vacancies.forEach(({year, demand}: Vacancy) => {
                yearDemand[year] = (yearDemand[year] || 0) + demand
            })

            ENTRANCE_YEARS.map(year => {
                const demand = yearDemand[year + Math.ceil(this.speciality.period)] || 0
                const target = this.targetRequestByYear[year] || 0

                this._entranceByYear[year] = target > demand
                    ? Math.ceil(target * this.entranceFactorTarget)
                    : Math.ceil((demand - target) * this.entranceFactorDemand + target * this.entranceFactorTarget)
            })
        }

        return this._entranceByYear
    }

    private total = (demandByYear: DemandByYear, years?: Array<number>, shift = 0) =>
        demandByYear
            ? Object.keys(demandByYear).map(y => parseInt(y)).filter(year => !years || (years.indexOf(year - shift) >= 0)).reduce((d, y) => d + (demandByYear[y] || 0), 0)
            : 0

    get demandTotal(): number {
        return this.total(this.demandByYear) + this.employees.filter(({fired}: Employee) => fired !== Fired.Fired).size
    }

    get traineeDemandTotal(): number {
        return this.total(this.traineeDemandByYear)
    }

    get traineeTotal(): number {
        return this.total(this.traineeByYear) + this.trainees.size
    }

    get targetTotal(): number {
        return this.total(this.targetByYear) + this.employees.filter(({target, fired}: Employee) => target && fired !== Fired.Fired).size
    }

    targetRequestTotal = (years?: number[], shift = 0): number => {
        return this.total(this.targetRequestByYear, years, shift)
    }

    entranceTotal = (years?: number[], shift = 0): number => {
        return this.total(this.entranceByYear, years, shift)
    }

    monthNames = (): string[] => {
        const monthNames = [
            'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
            'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
        ];
        return this.months.split('').map((digit, index) => digit === '1' && monthNames[index]).filter<string>((_): _ is string => !!_);
    }
}

export type JsonData =  {
    id: number
    companyId: number
    specialityId: number
    regionId: number
    employmentRegionId: number
    universityBranchId: number
    addBachelorDemand: true
    addBachelorTarget: boolean
    months: string
    isPracticePaid: boolean
    isPracticePaidName: string
    businessType: string
    businessTypeName: string
    nationalProject: string
    nationalProjectName: string
    englishLevel: string
    englishLevelName: string
    language: string
    languageName: string
    courseOfStudy: "Очная" | "Заочная" | "Очно-заочная"
    entranceFactorDemand: number
    entranceFactorTarget: number
    notes: string
    countryId: string
    countryName: string
    employees: EmployeeJsonData[]
    years: YearJsonData[]
    students: StudentJsonData[]
    vacancies: VacancyJsonData[]
    trainees: TraineeJsonData[]
}

export const fromJs = ({
    id,
    companyId,
    specialityId,
    regionId,
    employmentRegionId,
    universityBranchId,
    courseOfStudy,
    entranceFactorDemand,
    entranceFactorTarget,
    employees,
    years,
    vacancies,
    students,
    trainees,
    addBachelorDemand,
    addBachelorTarget,
    months,
    isPracticePaid,
    isPracticePaidName,
    businessType,
    businessTypeName,
    nationalProject,
    nationalProjectName,
    englishLevel,
    englishLevelName,
    language,
    languageName,
    notes,
    countryId,
    countryName,
}: JsonData, specialities: Specialities): Demand => new Demand({
    id,
    companyId,
    specialityId,
    regionId,
    employmentRegionId,
    nationalProject,
    nationalProjectName,
    englishLevel,
    englishLevelName,
    language,
    languageName,
    universityBranchId,
    addBachelorDemand,
    addBachelorTarget,
    months,
    isPracticePaid,
    isPracticePaidName,
    businessType,
    businessTypeName,
    courseOfStudy: courseOfStudyStringToEnum[courseOfStudy],
    entranceFactorDemand,
    entranceFactorTarget,
    notes,
    countryId,
    countryName,
    employees: <Employees>Map(employees.map(employee => [employee.id, employeeFromJs(employee)])).filter(({demandYear}: Employee) => demandYear === EMPLOYEE_YEAR),
    years: <Years>Map(years.map(year => [year.id, yearFromJs(year)])),
    students: <Students>Map(students.map(student => [student.id, studentFromJs(student)])),
    vacancies: <Vacancies>Map(vacancies.map(vacancy => [vacancy.id, vacancyFromJs(vacancy)])),//.filter(({year}: Vacancy) => year !== EMPLOYEE_YEAR),
    trainees: <Trainees>Map(trainees.map(trainee => [trainee.id, traineeFromJs(trainee)])),
    speciality: specialities.get(specialityId),
})


export type DemandList = Map<number, Demand>